import { CORE_COLOR_MAP } from 'Angular/constants/core.constants';
/**
 * Resampling frequencies the endpoint accepts. Values match
 * `UniverseQuartileChartFrequency` on the backend.
 */
export var UniverseQuartileChartFrequency;
(function (UniverseQuartileChartFrequency) {
    UniverseQuartileChartFrequency["NONE"] = "";
    UniverseQuartileChartFrequency["MONTHLY"] = "monthly";
    UniverseQuartileChartFrequency["QUARTERLY"] = "quarterly";
    UniverseQuartileChartFrequency["YEARLY"] = "yearly";
})(UniverseQuartileChartFrequency || (UniverseQuartileChartFrequency = {}));
/** Series ids, so the component can look a series up on the chart without going by index. */
export const UNIVERSE_SERIES_ID = 'universe';
export const BENCHMARK_SERIES_ID = 'benchmark';
export const TARGET_SERIES_ID_PREFIX = 'target-';
/**
 * Band fills, lightest at the top of the distribution darkening downward.
 */
export const UNIVERSE_TOP_BOX_COLOR = '#cccccc';
export const UNIVERSE_THIRD_BOX_COLOR = '#999999';
export const UNIVERSE_SECOND_BOX_COLOR = '#7f7f7f';
export const UNIVERSE_BOTTOM_BOX_COLOR = '#4c4c4c';
/**
 * The bands the universe is drawn as, bottom to top.
 *
 * A `columnrange` point holds one range -- a low and a high -- where a boxplot point held five
 * percentiles, so the five are drawn as the four bands between them. The median needs no mark of
 * its own: it is where the two middle bands meet.
 */
export const UNIVERSE_BANDS = [
    {
        id: `${UNIVERSE_SERIES_ID}-p05-q1`,
        name: '5th to 25th',
        from: 'low',
        to: 'q1',
        color: UNIVERSE_BOTTOM_BOX_COLOR,
        bulletColor: 'gray-30',
    },
    {
        id: `${UNIVERSE_SERIES_ID}-q1-median`,
        name: '25th to 50th',
        from: 'q1',
        to: 'median',
        color: UNIVERSE_SECOND_BOX_COLOR,
        bulletColor: 'gray-50',
    },
    {
        id: `${UNIVERSE_SERIES_ID}-median-q3`,
        name: '50th to 75th',
        from: 'median',
        to: 'q3',
        color: UNIVERSE_THIRD_BOX_COLOR,
        bulletColor: 'gray-60',
    },
    {
        id: `${UNIVERSE_SERIES_ID}-q3-p95`,
        name: '75th to 95th',
        from: 'q3',
        to: 'high',
        color: UNIVERSE_TOP_BOX_COLOR,
        bulletColor: 'gray-80',
    },
];
/** Targets cycle through the core palette; the benchmark sits deliberately outside it. */
export const TARGET_COLOR_INDICES = [1, 8, 7, 5, 2];
export const TARGET_COLORS = TARGET_COLOR_INDICES.map((n) => CORE_COLOR_MAP[n].code);
export const BENCHMARK_COLOR = '#FFA870';
/**
 * Shown in place of a percentile for a security the selected list does not contain -- a target
 * charted as an outsider, or the benchmark, which is never ranked.
 */
export const NOT_RANKED_LABEL = 'not ranked';
/** Fallback for an empty chart the endpoint gave no reason for. */
export const NO_DATA_DEFAULT_MESSAGE = 'No securities in this universe report this metric.';
/** Appended to an empty message whose cause the user can chart their way out of. */
export const NO_DATA_HINT = 'Try a longer timeframe or a different metric.';
export const GENERIC_ERROR_MESSAGE = 'Something went wrong loading this chart.';
/**
 * Metrics without history come back as a single period. There is nothing to plot along time, so
 * the chart falls back to a single box on a category axis.
 */
export const LATEST_CATEGORY_LABEL = 'Latest';
export const HIGHCHARTS_UNIVERSE_COLUMNRANGE_DEFAULT_OPTIONS = {
    chart: {
        // The strip the range selector overlays, and the axis width it is padded by in the markup.
        marginTop: 36,
        marginLeft: 55,
        spacingTop: 0,
        spacingLeft: 0,
        style: {
            fontFamily: 'inherit',
        },
        animation: false,
        backgroundColor: 'transparent',
        plotBackgroundColor: 'transparent',
        zooming: { type: 'x' },
    },
    title: {
        text: '',
    },
    legend: {
        enabled: false,
    },
    xAxis: {
        // Ticks sit between the categories, so the lines separate the bars.
        gridLineColor: '#e5e5e5',
        gridLineWidth: 1,
        lineWidth: 0,
        tickWidth: 0,
    },
    yAxis: {
        gridLineColor: '#e5e5e5',
        title: {
            text: '',
        },
        plotLines: [
            {
                value: 0,
                width: 1,
                color: '#999999',
                // Above the series — the default puts the line under the bars.
                zIndex: 5,
            },
        ],
    },
    plotOptions: {
        columnrange: {
            // The four bands share one slot instead of being laid side by side.
            grouping: false,
            groupPadding: 0,
            // A share of the slot per side, so 0.2 is the design's 3:2 bar to gap.
            pointPadding: 0.2,
            maxPointWidth: 120,
            borderWidth: 0,
            // Highcharts 11 rounds corners, which reads as a seam between bands.
            borderRadius: 0,
            states: {
                inactive: {
                    opacity: 1,
                },
            },
        },
        scatter: {
            marker: {
                symbol: 'diamond',
                // Half the diamond diagonal from the design; the symbol is drawn 2r wide.
                radius: 8,
                lineWidth: 2,
                lineColor: '#fff',
                states: {
                    hover: {
                        radius: 8,
                        lineWidthPlus: 0,
                    },
                },
            },
            states: {
                inactive: {
                    opacity: 1,
                },
            },
        },
    },
    tooltip: {
        useHTML: true,
        style: {
            fontSize: 'inherit',
        },
    },
    credits: {
        enabled: false,
    },
};
